// apolo/src/lib/loop/classifyReply.js — E4-WS1-T02 (code-tab-to-9).
// @arch:pure-engine
//
// PURE decision module: classifies a tool-less LLM reply into an action
// descriptor the spine switch-cases on. Extracted from the inline if/else
// chain in runLocalAgentLoopInner's tool-less branch (L-1 extract-not-rewrite;
// L-3 same predicates, same order). The spine keeps the side-effect execution
// (convo push, event emit, return); only the DECISION moved here.

import { decideNudge } from './decideNudge.js';
import { permitsCommitNudge } from '../runtime/deliveryScope.js';

/**
 * Classify a tool-less reply into an action descriptor.
 *
 * @param {object} s                    — SpineState (from initSpineState)
 * @param {string} content              — LLM reply text
 * @param {{ planMode?: boolean, isConversationalTask?: boolean, readOnly?: boolean, fastGenerative?: boolean, doneClaimBlockOn?: boolean, maxStallNudges?: number, claimsUnbackedGreen?: (text: string) => boolean, claimsUnbackedWrite?: (text: string) => boolean }} [opts]
 * @returns {{ kind: string, [key: string]: any }}
 */
export function classifyToollessReply(s, content, opts) {
  opts = opts || {};
  const { planMode, isConversationalTask, readOnly = false, fastGenerative, doneClaimBlockOn, maxStallNudges = 10, claimsUnbackedGreen, claimsUnbackedWrite } = opts;

  // 1. Plan mode — nudge once for present_plan, then accept prose.
  // WS-C / borda F3 (incidente tastedesign): um turno de plano com content VAZIO
  // (ex.: reasoning-only) e sem tool executada caía direto em plan_done "oco" — cobrava
  // mas não entregava texto NEM card. Agora esse turno vazio também leva o nudge de uma
  // vez (present_plan). O guard s.planNudged mantém tudo one-shot (sem loop).
  if (planMode) {
    if (!s.planNudged && (content.trim() || !s.executedAnyTool)) return { kind: 'plan_nudge' };
    return { kind: 'plan_done' };
  }

  // 2. Conversational task — the text answer IS the deliverable.
  // F13-E7-T06 — exceto se o turno ESCREVEU arquivo: aí os gates de entrega
  // (audit/oracle/green-claim) valem mesmo em tarefa conversacional/chat.
  if (isConversationalTask && content.trim() && !s.wroteFile) return { kind: 'conversational_done' };

  // 3. Nudge gates (6 ordered gates via decideNudge — already extracted P4-T01).
  const lastToolFailed = s.convo.some(
    (m) => m.role === 'tool' && typeof m.content === 'string' && /^Erro/i.test(m.content),
  );
  const nudge = decideNudge({
    content, executedAnyTool: s.executedAnyTool, wroteFile: s.wroteFile, committed: s.committed,
    stallNudged: s.stallNudged, toolFailureNudged: s.toolFailureNudged, commitNudged: s.commitNudged,
    lastToolFailed, iteration: s.iteration, maxStallNudges,
    readOnly, // dogfood 2026-07-10 — EXPLORE read-only não recebe nudge de write/commit
    allowCommitNudge: permitsCommitNudge(s.convo),
  });
  if (nudge) {
    if ('stop' in nudge) return { kind: 'nudge_stop', stopReason: nudge.stop };
    return { kind: 'nudge', nudge };
  }

  // 4. Deliverable audit (html/css files need audit before done).
  const hasWebFiles = s.writtenFiles.some((f) => /\.(html?|css)$/i.test(f.path));
  if (!s.auditNudged && hasWebFiles) return { kind: 'audit_check' };

  // 5. Auto-correct oracle — fast-generative announces skip; normal checks oracle.
  const hasCodeFiles = s.writtenFiles.some((f) => /\.(jsx?|tsx?|css)$/i.test(f.path));
  if (fastGenerative && !s.generativeFastAnnounced && hasCodeFiles) {
    return { kind: 'generative_fast_announce' };
  }
  if (!s.autoCorrectGreen && !fastGenerative && hasCodeFiles) {
    return { kind: 'auto_correct_check' };
  }

  // 6. Unverified deliverable check (non-code files not covered by oracle).
  const oracleGreen = s.autoCorrectResult && s.autoCorrectResult.reachedGreen;
  let unverifiedDeliverable = null;
  if (s.writtenFiles.length && !oracleGreen) {
    const nonCode = s.writtenFiles.filter((f) => !/\.(jsx?|tsx?|css)$/i.test(f.path));
    const deliverableFindings = [];
    const unverified = [];
    for (const f of nonCode) {
      if (/\.json$/i.test(f.path)) {
        try { JSON.parse(String(f.content ?? '')); }
        catch (e) { deliverableFindings.push({ file: f.path, severity: 'error', message: `JSON inválido: ${e?.message || 'parse error'}` }); }
      } else if (!/\.(html?|css)$/i.test(f.path)) {
        unverified.push(f.path);
      }
    }
    if (deliverableFindings.length || unverified.length) {
      unverifiedDeliverable = { findings: deliverableFindings, unverified };
    }
  }

  // 7. Unbacked green claim block.
  if (doneClaimBlockOn && !oracleGreen && typeof claimsUnbackedGreen === 'function' && claimsUnbackedGreen(content)) {
    if (!s.greenClaimNudged) return { kind: 'green_claim_nudge' };
    return { kind: 'green_claim_block' };
  }

  // 7b. F13-E14-T01 — tool-before-claim (leak Fable-5: "if you don't use the
  // tool, you are lying"): afirmou ter criado/editado ARQUIVO sem nenhuma tool
  // de escrita neste turno → 1 nudge corretivo (execute ou retire o claim).
  // harness-audit 07-13 — antes a reincidência caía pro done (advisory); agora, espelhando
  // o green-claim (§7), a mentira que PERSISTE após o nudge BLOQUEIA com 'unbacked_write_claim'.
  if (doneClaimBlockOn && !s.wroteFile
      && typeof claimsUnbackedWrite === 'function' && claimsUnbackedWrite(content)) {
    return s.actionClaimNudged ? { kind: 'action_claim_block' } : { kind: 'action_claim_nudge' };
  }

  // 8. Done — model finished with content, no tool calls needed.
  return { kind: 'done', unverifiedDeliverable };
}
